import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/store/store'
import type { LexicalDecision } from '../workflow/sentenceFlowModel'
import type { ReadingLexicalBatchMode, ReadingLexicalDraft } from '../authoring/readingLexicalBatchModel'

interface ReadingLexicalBatchState {
  open: boolean
  readingId: string | null
  step: number
  furthestStep: number
  singleDrafts: ReadingLexicalDraft[]
  phraseDrafts: ReadingLexicalDraft[]
}

const initialState: ReadingLexicalBatchState = {
  open: false,
  readingId: null,
  step: 0,
  furthestStep: 0,
  singleDrafts: [],
  phraseDrafts: [],
}

function draftsFor(state: ReadingLexicalBatchState, mode: ReadingLexicalBatchMode) {
  return mode === 'single' ? state.singleDrafts : state.phraseDrafts
}

const readingLexicalBatchSlice = createSlice({
  name: 'readingLexicalBatch',
  initialState,
  reducers: {
    openReadingLexicalBatch(state, action: PayloadAction<{ readingId: string; singleDrafts: ReadingLexicalDraft[] }>) {
      const sameReading = state.readingId === action.payload.readingId
      state.open = true
      state.readingId = action.payload.readingId
      if (!sameReading) {
        state.step = 0
        state.furthestStep = 0
        state.singleDrafts = action.payload.singleDrafts
        state.phraseDrafts = []
      }
    },
    closeReadingLexicalBatch(state) { state.open = false },
    setReadingLexicalBatchStep(state, action: PayloadAction<number>) {
      state.step = Math.max(0, Math.min(4, action.payload))
      state.furthestStep = Math.max(state.furthestStep, state.step)
    },
    setReadingLexicalBatchDrafts(state, action: PayloadAction<{ mode: ReadingLexicalBatchMode; drafts: ReadingLexicalDraft[] }>) {
      if (action.payload.mode === 'single') state.singleDrafts = action.payload.drafts
      else state.phraseDrafts = action.payload.drafts
    },
    updateReadingLexicalBatchDraft(state, action: PayloadAction<{
      mode: ReadingLexicalBatchMode
      batchKey: string
      translationVi?: string
      decision?: LexicalDecision | null
    }>) {
      const drafts = draftsFor(state, action.payload.mode)
      const draft = drafts.find((item) => item.batchKey === action.payload.batchKey)
      if (!draft) return
      if (action.payload.translationVi !== undefined) draft.translationVi = action.payload.translationVi
      if (action.payload.decision !== undefined) {
        const normalizedText = draft.text.trim().toLocaleLowerCase('en')
        const normalizedMeaning = draft.translationVi.trim().toLocaleLowerCase('vi')
        for (const item of drafts) {
          if (
            item.text.trim().toLocaleLowerCase('en') === normalizedText
            && item.translationVi.trim().toLocaleLowerCase('vi') === normalizedMeaning
          ) item.decision = action.payload.decision
        }
      }
    },
    removeReadingLexicalBatchDraft(state, action: PayloadAction<{ mode: ReadingLexicalBatchMode; batchKey: string }>) {
      if (action.payload.mode === 'single') return
      state.phraseDrafts = state.phraseDrafts.filter((item) => item.batchKey !== action.payload.batchKey)
    },
  },
})

export const {
  openReadingLexicalBatch,
  closeReadingLexicalBatch,
  setReadingLexicalBatchStep,
  setReadingLexicalBatchDrafts,
  updateReadingLexicalBatchDraft,
  removeReadingLexicalBatchDraft,
} = readingLexicalBatchSlice.actions

export const selectReadingLexicalBatch = (state: RootState) => state.readingLexicalBatch
export default readingLexicalBatchSlice.reducer
